<template>
  <div class="bg__beige ly--outer--container discount--bar">
    <ImageWrapper
      src="/assets/images/2025__discount_bar_bg.png"
      alt=""
      class="w-full banner__bg"
    />
    <div class="ly--content--container relative discount--content">
      <!-- Discount offers -->
      <div class="discount--offers">
        <template v-for="(offer, index) in discountOffers">
          <!-- Connection line (render before offer content if not first or last) -->
          <div v-if="index !== 0" class="offer--line">
            <ImageWrapper :src="offer.lineImage" alt="" />
          </div>

          <!-- Discount offer -->
          <div class="discount--offer" :class="`offer--${index + 1}`">
            <!-- Offer content wrapper -->
            <div
              class="offer--content"
              :class="{ selected: index === selected }"
              @click="scrollToSection(index)"
              role="button"
              tabindex="0"
              @keydown.enter="scrollToSection(index)"
              @keydown.space.prevent="scrollToSection(index)"
              :aria-label="`${offer.discount} - ${offer.title}`"
            >
              <!-- Offer icon -->
              <div class="offer--icon">
                <ImageWrapper
                  :src="offer.icon"
                  :alt="offer.title"
                  :isMask="true"
                  class="icon"
                />
              </div>

              <!-- Offer text -->
              <div class="offer--text">
                <p class="offer--discount">{{ offer.discount }}</p>
                <p class="offer--title">{{ offer.title }}</p>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ImageWrapper } from "~/components/";

export default {
  name: "DiscountBar",

  components: {
    ImageWrapper,
  },

  /**
   * Component props definition
   */
  props: {},

  /**
   * Component data initialization
   * @returns {Object} Component data object containing discount offers array
   */
  data() {
    return {
      selected: 0,
      /**
       * Array of discount offers with their respective details
       * @type {Array<Object>}
       */
      discountOffers: [
        {
          discount: this.$t("discountBar.1.discount"),
          title: this.$t("discountBar.1.title"),
          icon: "/assets/images/2025/bar_landing.png",
          lineImage: "/assets/images/line-18.svg",
        },
        {
          discount: this.$t("discountBar.2.discount"),
          title: this.$t("discountBar.2.title"),
          icon: "/assets/images/2025/bar_product.png",
          lineImage: "/assets/images/line-18.svg",
        },
        {
          discount: this.$t("discountBar.3.discount"),
          title: this.$t("discountBar.3.title"),
          icon: "/assets/images/2025/bar_addon.png",
          lineImage: "/assets/images/line-19.svg",
        },
      ],
      // IDs of sections we track for center-of-screen detection
      sectionIds: ["banner", "product-section", "addon-section"],
      _scrollRaf: null,
      _onScrollHandler: null,
    };
  },

  /**
   * Component computed properties
   */
  computed: {},

  /**
   * Bind scroll/resize events and initialize selection
   */
  mounted() {
    this._onScrollHandler = this.onScroll.bind(this);
    window.addEventListener("scroll", this._onScrollHandler, { passive: true });
    window.addEventListener("resize", this._onScrollHandler, { passive: true });
    this.onScroll();
  },

  /**
   * Cleanup listeners
   */
  beforeDestroy() {
    if (this._onScrollHandler) {
      window.removeEventListener("scroll", this._onScrollHandler);
      window.removeEventListener("resize", this._onScrollHandler);
      this._onScrollHandler = null;
    }
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = null;
    }
  },

  /**
   * Component methods
   */
  methods: {
    /**
     * Scroll to the corresponding section based on the offer index
     * @param {number} index - The index of the clicked offer
     */
    scrollToSection(index) {
      const sectionId = this.sectionIds[index];
      if (sectionId) {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }
    },

    // Get DOM elements for tracked sections
    getTrackedSections() {
      return this.sectionIds
        .map((id) =>
          typeof document !== "undefined" ? document.getElementById(id) : null,
        )
        .filter((el) => !!el);
    },

    // Determine which section currently contains the viewport center
    // Activate next section when previous section's bottom passes 1/3 from top
    computeActiveIndex() {
      const thresholdY = window.innerHeight / 3;
      const sections = this.getTrackedSections();

      if (!sections.length) return 0;

      // Count how many sections' bottoms are above or at the threshold line
      const countPassed = sections.reduce((count, el) => {
        const rect = el.getBoundingClientRect();
        return count + (rect.bottom <= thresholdY ? 1 : 0);
      }, 0);

      // Active index equals number of passed sections, clamped to last index
      return Math.min(countPassed, sections.length - 1);
    },

    // Throttled via rAF to avoid excessive recalculations
    onScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        const idx = this.computeActiveIndex();
        if (idx !== this.selected) {
          this.selected = idx;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~/scss/include/_commonInclude.scss";

.content .app {
  .discount--bar {
    position: sticky;
    top: rem(125);
    height: rem(81);
    z-index: 110;
    overflow: hidden;
    // position: relative;
    @include media-breakpoint-down(md) {
      // top: rem(79);
      top: 2.9375rem;
    }

    overflow: visible;
    filter: drop-shadow(0 4px 4px rgba(0, 0, 0, 0.3))
      drop-shadow(0 8px 12px rgba(0, 0, 0, 0.15));

    .banner__bg {
      width: 100%;
      height: rem(108);
      position: absolute;
      object-fit: cover;
      object-position: top;
      top: rem(-10);
      left: 50%;
      transform: translateX(-50%);
    }

    .discount--content {
      height: 100%;
      display: flex;
      align-items: start;
      justify-content: center;
    }

    .discount--circle {
      position: absolute;
      left: 33.333%;
      top: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;

      .circle--bg {
        width: rem(50);
        height: rem(50);
        display: block;
      }

      .circle--icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: rem(36);
        height: rem(36);
      }
    }

    .discount--offers {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: -1.2%;
      column-gap: rem(24);

      &__content {
        display: inline-flex;
        align-items: center;
        position: relative;
        // padding-right: rem(24+ 48);
        // padding-left: rem(24);

        // &:after {
        //   content: "";
        //   position: absolute;
        //   top: 50%;
        //   right: 0;
        //   transform: translateY(-50%);
        //   width: rem(48);
        //   height: rem(1);
        //   background: white;
        // }
      }

      .offer--wrapper {
        display: flex;
        align-items: center;
        gap: rem(8);
      }

      .offer--line {
        width: rem(48);
        height: rem(1);

        img {
          width: 100%;
          height: 100%;
          display: block;
        }
      }
    }

    .discount--offer {
      display: flex;
      align-items: center;

      .offer--content {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        cursor: pointer;
        transition: transform 0.2s ease;

        &:hover {
          transform: translateY(-2px);
        }

        &:focus {
          outline: 2px solid rgba(255, 255, 255, 0.8);
          outline-offset: 2px;
          border-radius: rem(4);
        }
      }

      .offer--icon {
        width: rem(40);
        height: rem(40);
        border-radius: rem(40);
        background: rgba(208, 188, 174, 0.5);
        backdrop-filter: blur(rem(5));
        display: flex;
        align-items: center;
        justify-content: center;
        padding: rem(6);
        flex-shrink: 0;
        position: relative;

        .icon {
          width: rem(22);
          aspect-ratio: 1;
          display: block;
          background: white;
          mask-size: contain;
        }
      }

      .selected {
        .offer--icon {
          // background-color: white;
          &::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 73%;
            border-radius: 2.5rem;
            background-color: white;
            z-index: 1;
            aspect-ratio: 1;
          }
        }
        .icon {
          position: relative;
          background-color: #a53939;
          z-index: 2;
        }
      }

      .offer--text {
        position: absolute;
        top: calc(100% + #{rem(6)});
        left: 50%;
        transform: translateX(-50%);
        text-align: center;
        white-space: nowrap;

        .offer--discount {
          font-family: "Nespresso_Lucas", sans-serif;
          font-weight: bold;
          font-size: rem(11);
          line-height: 1.13;
          text-transform: uppercase;
          color: color(white);
          margin: 0;
        }

        .offer--title {
          position: absolute;
          top: calc(100% + #{rem(2)});
          left: 50%;
          transform: translateX(-50%);
          font-family: "Nespresso_Lucas", sans-serif;
          font-weight: bold;
          font-size: rem(11);
          line-height: 1.13;
          text-transform: uppercase;
          color: color(white);
          margin: 0;
          white-space: nowrap;
        }
      }
    }

    // Responsive adjustments
    @include media-breakpoint-down(md) {
      .discount--offers {
        padding: 0 rem(10);
        gap: rem(16);
      }

      .discount--offer {
        gap: rem(16);
      }
    }
  }
}
</style>
